import { useEffect, useMemo, useState } from 'react';
import type { AutomationStep } from '../types/automation';

/**
 * Schema data the flow builder needs to drive variable pickers and step
 * config. Extracted from `ListFlowBuilder` (Phase 0 of the builder 2.0 work)
 * so both the 1.0 builder and the new 2.0 builder consume identical loading
 * logic rather than each maintaining its own copy.
 *
 * - `stepTypeSchemas` / `stepTypeLabels` — from the step-type registry
 * - `dataLoaderSchemas` — dynamic output schema for `load_data` steps, keyed
 *   by `dataType`
 * - `agentOutputSchemas` — per-agent `collectedVariables.<name>` schema
 *   entries, keyed by `agentId`, derived from each bound agent's Data
 *   Contract (`metadata.outputVariables`)
 *
 * The schema-value records are intentionally typed loosely (`Record<string,
 * any>`) — that's the existing contract `ListFlowBuilder` and the variable
 * picker rely on; tightening it is out of scope for a behaviour-preserving
 * extraction.
 */
export interface StepSchemas {
	stepTypeSchemas: Map<string, Record<string, any>>;
	stepTypeLabels: Map<string, string>;
	dataLoaderSchemas: Map<string, Record<string, any>>;
	agentOutputSchemas: Map<number, Record<string, any>>;
}

/**
 * Loads and maintains the schema maps for a flow's steps.
 *
 * @param steps - the flow's current steps; used to derive which agents are
 *   referenced by `agent_call` steps so their output schemas can be loaded.
 */
export function useStepSchemas(steps: Array<AutomationStep>): StepSchemas {
	const [stepTypeSchemas, setStepTypeSchemas] = useState<
		Map<string, Record<string, any>>
	>(new Map());
	const [stepTypeLabels, setStepTypeLabels] = useState<Map<string, string>>(
		new Map(),
	);
	const [dataLoaderSchemas, setDataLoaderSchemas] = useState<
		Map<string, Record<string, any>>
	>(new Map());
	// Per-agent output schemas keyed by agentId. Each entry is a partial
	// schema map of `collectedVariables.<name>` -> field metadata, derived
	// from the bound agent's `metadata.outputVariables` declarations. Merged
	// into the agent_call step's static schema at variable-picker build time
	// so downstream steps can autocomplete on the actual variable names.
	const [agentOutputSchemas, setAgentOutputSchemas] = useState<
		Map<number, Record<string, any>>
	>(new Map());

	// Load step type schemas, labels, and data loader schemas.
	useEffect(() => {
		const loadStepTypes = async () => {
			try {
				const { AutomationApiService } = await import(
					'../services/automationApi'
				);
				const types = await AutomationApiService.getStepTypes();
				const schemaMap = new Map<string, Record<string, any>>();
				const labelMap = new Map<string, string>();

				types.forEach((type) => {
					if (type.outputSchema != null) {
						schemaMap.set(type.type, type.outputSchema);
					}

					labelMap.set(type.type, type.name);
				});

				setStepTypeSchemas(schemaMap);
				setStepTypeLabels(labelMap);
				console.debug('Loaded step type schemas for list builder:', schemaMap);
				console.debug('Loaded step type labels for list builder:', labelMap);

				// Also load data loader types for load_data step dynamic schemas
				const loaderTypes = await AutomationApiService.getDataLoaderTypes();
				const loaderSchemaMap = new Map<string, Record<string, any>>();

				loaderTypes.forEach((loader) => {
					if (loader.outputSchema != null) {
						loaderSchemaMap.set(loader.dataType, loader.outputSchema);
					}
				});

				setDataLoaderSchemas(loaderSchemaMap);
				console.debug(
					'Loaded data loader schemas for list builder:',
					loaderSchemaMap,
				);
			} catch (error) {
				console.error('Failed to load step types:', error);
			}
		};

		loadStepTypes();
	}, []);

	// Stable key for the set of agent IDs bound to agent_call steps in this
	// flow. Used to drive the per-agent schema loader without re-running it
	// every time any unrelated step config changes.
	const referencedAgentIdsKey = useMemo(() => {
		const ids = new Set<number>();
		for (const step of steps) {
			if (step.stepType === 'agent_call') {
				const agentId = (step.config as any)?.agentId;
				if (typeof agentId === 'number') {
					ids.add(agentId);
				}
			}
		}
		return Array.from(ids)
			.sort((a, b) => a - b)
			.join(',');
	}, [steps]);

	// Load each bound agent's output-variable declarations and convert them
	// to schema-entry shape so the variable picker shows them as selectable
	// fields under `collectedVariables.<name>` for downstream steps.
	useEffect(() => {
		if (referencedAgentIdsKey === '') {
			setAgentOutputSchemas(new Map());
			return;
		}

		const agentIds = referencedAgentIdsKey.split(',').map(Number);
		let cancelled = false;

		const loadAgents = async () => {
			try {
				const { AgentService } = await import('../services/agentService');
				const next = new Map<number, Record<string, any>>();

				await Promise.all(
					agentIds.map(async (agentId) => {
						try {
							const result = await AgentService.getAgent(agentId);
							if (!result.success || result.data == null) {
								return;
							}
							const metadata = (result.data as any).metadata as
								| Record<string, unknown>
								| undefined;
							const outputVariables = metadata?.['outputVariables'] as
								| Array<{
										name: string;
										label?: string;
										description?: string;
										type: string;
										arrayType?: string;
										objectType?: string;
								  }>
								| undefined;

							if (outputVariables == null || outputVariables.length === 0) {
								return;
							}

							const entries: Record<string, any> = {};
							for (const variable of outputVariables) {
								entries[`collectedVariables.${variable.name}`] = {
									type: variable.type,
									label:
										variable.label != null && variable.label !== ''
											? variable.label
											: variable.name,
									description:
										variable.description != null && variable.description !== ''
											? variable.description
											: `Collected from this call by the agent's data contract`,
									...(variable.arrayType != null
										? { arrayType: variable.arrayType }
										: {}),
									...(variable.objectType != null
										? { objectType: variable.objectType }
										: {}),
								};
							}
							next.set(agentId, entries);
						} catch (error) {
							console.error(
								`Failed to load agent ${agentId} for variable picker:`,
								error,
							);
						}
					}),
				);

				if (!cancelled) {
					setAgentOutputSchemas(next);
				}
			} catch (error) {
				console.error('Failed to load agent output schemas:', error);
			}
		};

		void loadAgents();

		return () => {
			cancelled = true;
		};
	}, [referencedAgentIdsKey]);

	return {
		stepTypeSchemas,
		stepTypeLabels,
		dataLoaderSchemas,
		agentOutputSchemas,
	};
}
